import React, { useState, useEffect } from "react";
import { useDeveloper } from "../../context/DeveloperContext";
import AdminPanel from "../admin/AdminPanel";
import { listSchools } from "../../api/schoolConfigApi";
import type { components } from "../../api/types.gen";
import styles from "./AdminPortalWrapper.module.css";

type SchoolResponse = components["schemas"]["SchoolResponse"];

/**
 * Wrapper component that allows developers to access admin functionality
 * for any organization by providing organization context override
 */
const AdminPortalWrapper = (): React.JSX.Element => {
  const { selectedOrgId, setSelectedOrgId, selectedOrg, setSelectedOrg } =
    useDeveloper();
  const [organizations, setOrganizations] = useState<SchoolResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  // Load all organizations
  useEffect(() => {
    const loadOrganizations = async () => {
      try {
        const schoolsResult = await listSchools({ limit: 100 });
        const orgsData = [...schoolsResult.data].sort((a, b) =>
          a.name.localeCompare(b.name),
        );

        setOrganizations(orgsData);
        setLoadError(false);

        // Don't auto-select - require explicit selection to prevent context issues
      } catch (error) {
        console.error("Failed to load organizations:", error);
        setLoadError(true);
      } finally {
        setLoading(false);
      }
    };

    loadOrganizations();
  }, []);

  // Update selected organization when ID changes. Always reconcile so that
  // deselecting (selectedOrgId -> null) or an unmatched id clears selectedOrg
  // instead of leaving AdminPanel mounted against a stale org.
  useEffect(() => {
    if (!selectedOrgId) {
      setSelectedOrg(null);
      return;
    }
    if (organizations.length > 0) {
      const org = organizations.find((o) => o.id === selectedOrgId);
      setSelectedOrg(org || null);
    }
  }, [selectedOrgId, organizations, setSelectedOrg]);

  if (loading) {
    return (
      <div className="admin-portal-wrapper">
        <div className={styles["loading-container"]}>
          <p>Loading organizations...</p>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="admin-portal-wrapper">
        <div className={styles["error-container"]} role="alert">
          <h3>Unable to Load Organizations</h3>
          <p>
            Something went wrong while loading organizations. Please refresh the
            page or try again later.
          </p>
        </div>
      </div>
    );
  }

  if (organizations.length === 0) {
    return (
      <div className="admin-portal-wrapper">
        <div className="no-orgs-container">
          <h3>No Organizations Found</h3>
          <p>
            Create an organization first to access admin portal functionality.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-portal-wrapper">
      {/* Developer Mode Banner */}
      <div
        className="developer-mode-banner"
        style={{
          backgroundColor: "#f0f8ff",
          border: "2px solid #4a90e2",
          borderRadius: "8px",
          padding: "12px 16px",
          marginBottom: "20px",
          textAlign: "center",
        }}
      >
        <strong>🔧 Developer Mode: Admin Portal</strong>
        {selectedOrg && (
          <span style={{ marginLeft: "16px", color: "#666" }}>
            Acting as Admin for: <strong>{selectedOrg.name}</strong>
          </span>
        )}
      </div>

      {/* Organization Selector */}
      <div
        className="developer-org-selector"
        style={{
          marginBottom: "16px",
          padding: "8px 12px",
          backgroundColor: "#f9f9f9",
          borderRadius: "6px",
          border: "1px solid #ddd",
          display: "inline-block",
          height: "auto",
          minHeight: "50px",
          width: "auto",
        }}
      >
        <label
          htmlFor="org-selector"
          style={{
            display: "block",
            marginBottom: "2px",
            fontWeight: "bold",
            color: "#333",
            fontSize: "12px",
          }}
        >
          Select Organization:
        </label>
        <select
          id="org-selector"
          value={selectedOrgId || ""}
          onChange={(e) =>
            setSelectedOrgId(e.target.value ? parseInt(e.target.value) : null)
          }
          style={{
            width: "300px",
            padding: "6px 8px",
            fontSize: "14px",
            border: "1px solid #ccc",
            borderRadius: "4px",
            backgroundColor: "white",
            height: "32px",
          }}
        >
          <option value="">-- Select Organization --</option>
          {organizations.map((org) => (
            <option key={org.id} value={org.id}>
              {org.name} (ID: {org.id})
            </option>
          ))}
        </select>
      </div>

      {/* Admin Panel with organization context override */}
      {selectedOrg && (
        <div className="admin-panel-container">
          <AdminPanel
            developerModeOrgId={selectedOrgId}
            organizationContext={{ currentOrgId: selectedOrgId }}
          />
        </div>
      )}
    </div>
  );
};

export default AdminPortalWrapper;
